import { ButtonCus } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { HONORIFIC_TITLE_SELECT } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { HONORIFIC_TITLE_TYPE } from '@/types/enums';
import { CommissionMonthYearOwner } from '@/types/http-payload/commission';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface IssueInvoiceFormProps {
    idModal: string,
    adjust: CommissionMonthYearOwner,
    isAdmin: boolean
}

export default observer(function IssueInvoiceForm({
    idModal,
    adjust,
    isAdmin
}: IssueInvoiceFormProps) {
    // hook
    const { t } = useTranslation();

    // store
    const {
        commissionMonthStore: {
            issueInvoiceBill,
            getSalutation
        },
        modalStore: { hideAllModals, hideModal, showAlertModal }
    } = useStore();

    // form
    const validateSchema = yup.object().shape({
        commissionBillTitle: yup
            .string()
            .trim(),
        commissionBillType: yup
            .string()
            .required('form_error_validate.required')

    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            commissionBillTitle: '',
            commissionBillType: HONORIFIC_TITLE_TYPE.PERSONAL
        }
    });

    const [disableInput, setDisableInput] = useState<boolean>(false);

    // lifecycle
    useEffect(() => {
        onFetch(adjust);
    }, [adjust]);

    // function
    const onFetch = async (adjust) => {
        if (adjust?.ownerId) {
            const res = await flowResult(getSalutation({
                userId: Number(adjust?.ownerId)
            }));
            if (res) {
                const { salutationTitle, salutationType, isChange } = res;
                form.reset({
                    commissionBillTitle: salutationTitle || '',
                    commissionBillType: salutationType || HONORIFIC_TITLE_TYPE.PERSONAL
                });
                if (!isAdmin && !isChange && salutationTitle && salutationType) {
                    setDisableInput(true);
                }
            }
        }
    };
    const onSubmit = async (data: FormData) => {
        const { commissionYear, commissionMonth, ownerId } = adjust;
        const payload = {
            ...data,
            commissionYear: commissionYear || '',
            commissionMonth: commissionMonth || '',
            ownerId: ownerId || ''
        };

        const res = await flowResult(issueInvoiceBill({ ...payload }));
        hideModal(idModal);
        if (res) {
            // hideAllModals();
            showAlertModal({
                id: 'export-success',
                type: 'success',
                content: t('messages.issue_export_file_success_cms')
            });
        }
    };

    const onCancel = () => {
        hideAllModals();
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className='grid grid-cols-1 xsm:grid-cols-2 gap-4'>
                    <FormField
                        control={form.control}
                        name='commissionBillTitle'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('field.issue_invoice.receipt_addressee')}</FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t('placeholder.enter')}
                                        disabled={disableInput}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='commissionBillType'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('field.issue_invoice.honorific_title')}</FormLabel>
                                <FormControl>
                                    <Combobox
                                        value={field.value?.toString() ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.code);
                                        }}
                                        items={HONORIFIC_TITLE_SELECT.map(item => ({
                                            label: t(item.name),
                                            value: item?.value?.toString(),
                                            code: item?.value
                                        }))}
                                        search={false}
                                        disableClearIcon
                                        required
                                        disabled={disableInput}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <div className='mt-6'>
                    バイクレンタル手数料表に宛名が空白の場合、所有者名が自動で入力されます。
                    <br />
                    会社名などを宛名としてご希望の場合は、宛名をご入力下さい。
                    {!isAdmin && (
                        <>
                            <br />
                            なお、一度入力した情報は変更することは出来ません。予めご注意下さい。
                        </>
                    )}
                </div>

                <div className='flex gap-4 mt-6 justify-center'>
                    <ButtonCus variant='outline' size='inModal' type='button' onClick={onCancel}>
                        {t('button.cancel')}
                    </ButtonCus>
                    <ButtonCus variant='default' size='inModal' type='submit'>
                        {t('button.issuance_of_fee_list')}
                    </ButtonCus>
                </div>
            </form>
        </Form>
    );
});
